import React, { Component } from "react";
import PropTypes from "prop-types";

import GenericChartComponent from "../GenericChartComponent";
import { getAxisCanvas, getMouseCanvas } from "../GenericComponent";
import { hexToRGBA, getStrokeDasharray } from "../utils";

class SupertrendSeries extends Component {
  constructor(props) {
    super(props);
    this.yAccessorValue = this.yAccessorValue.bind(this);
    this.yAccessorUp = this.yAccessorUp.bind(this);
    this.yAccessorDown = this.yAccessorDown.bind(this);
    this.renderSVG = this.renderSVG.bind(this);
    this.drawOnCanvas = this.drawOnCanvas.bind(this);
    this.normalizeStroke = this.normalizeStroke.bind(this);
    this.getDirectionalSegments = this.getDirectionalSegments.bind(this);
    this.isHover = this.isHover.bind(this);
  }

  yAccessorValue(d) {
    const { yAccessor } = this.props;
    const node = yAccessor ? yAccessor(d) : undefined;
    return node ? node.value : undefined;
  }

  yAccessorUp(d) {
    const { yAccessor } = this.props;
    const node = yAccessor ? yAccessor(d) : undefined;
    if (!node) return undefined;
    return node.direction > 0 ? node.uptrend : undefined;
  }

  yAccessorDown(d) {
    const { yAccessor } = this.props;
    const node = yAccessor ? yAccessor(d) : undefined;
    if (!node) return undefined;
    return node.direction <= 0 ? node.downtrend : undefined;
  }

  normalizeStroke(stroke, fallback) {
    if (typeof stroke === "string") {
      return { ...fallback, color: stroke };
    }
    return {
      color: stroke?.color || fallback.color,
      width: stroke?.width ?? fallback.width,
      dash: stroke?.dash || fallback.dash,
      opacity: stroke?.opacity ?? fallback.opacity,
    };
  }

  getDirectionalSegments(moreProps) {
    const {
      plotData,
      xScale,
      xAccessor,
      chartConfig: { yScale },
    } = moreProps;
    const { yAccessor } = this.props;
    const segments = [];
    const pivots = [];

    let currentSegment = null;
    let lastStrokeKey = null;

    for (let i = 0; i < plotData.length; i += 1) {
      const datum = plotData[i];
      const node = yAccessor ? yAccessor(datum) : undefined;

      if (
        !node ||
        !Number.isFinite(node.uptrend) ||
        !Number.isFinite(node.downtrend)
      ) {
        currentSegment = null;
        lastStrokeKey = null;
        continue;
      }

      const strokeKey = node.direction > 0 ? "up" : "down";
      const value = node.direction > 0 ? node.uptrend : node.downtrend;

      if (!Number.isFinite(value)) {
        currentSegment = null;
        lastStrokeKey = null;
        continue;
      }

      const scaledX = xScale(xAccessor(datum));
      const scaledY = yScale(value);
      if (!Number.isFinite(scaledX) || !Number.isFinite(scaledY)) {
        currentSegment = null;
        lastStrokeKey = null;
        continue;
      }

      const point = {
        x: scaledX,
        y: scaledY,
      };

      if (strokeKey !== lastStrokeKey) {
        const previousSegment = segments.length
          ? segments[segments.length - 1]
          : null;
        const pivotPoint =
          previousSegment && previousSegment.points.length
            ? previousSegment.points[previousSegment.points.length - 1]
            : null;

        currentSegment = {
          key: strokeKey,
          points: pivotPoint ? [pivotPoint, point] : [point],
        };
        segments.push(currentSegment);

        if (pivotPoint && lastStrokeKey !== null) {
          const rawReferencePrice =
            strokeKey === "up"
              ? Number.isFinite(datum.low)
                ? datum.low
                : (datum.close ?? datum.high ?? datum.low)
              : Number.isFinite(datum.high)
                ? datum.high
                : (datum.close ?? datum.high ?? datum.low);
          const referencePrice = Number.isFinite(rawReferencePrice)
            ? rawReferencePrice
            : value;
          const priceY = yScale(referencePrice);

          if (Number.isFinite(priceY)) {
            pivots.push({
              key: strokeKey,
              x: point.x,
              priceY,
              referencePrice,
              point: { x: point.x, y: priceY },
            });
          }
        }
      } else if (!currentSegment) {
        currentSegment = {
          key: strokeKey,
          points: [point],
        };
        segments.push(currentSegment);
      } else {
        currentSegment.points.push(point);
      }

      lastStrokeKey = strokeKey;
    }

    return { segments, pivots };
  }

  renderSVG(moreProps) {
    const { className, onDoubleClick, onContextMenu, upStroke, downStroke } =
      this.props;
    const upStrokeProps = this.normalizeStroke(upStroke, {
      color: "#8cc176",
      width: 2,
      dash: "Solid",
      opacity: 1,
    });
    const downStrokeProps = this.normalizeStroke(downStroke, {
      color: "#b82c0c",
      width: 2,
      dash: "Solid",
      opacity: 1,
    });

    const { segments, pivots } = this.getDirectionalSegments(moreProps);
    const { hovering } = moreProps;

    const paths = [];

    segments.forEach((segment, idx) => {
      if (!segment.points.length) return;
      const strokeProps =
        segment.key === "up" ? upStrokeProps : downStrokeProps;
      const strokeWidth = hovering
        ? (strokeProps.width + 1) * 1.5
        : strokeProps.width;
      const strokeOpacity = strokeProps.opacity ?? 1;
      const d = segment.points
        .map(
          (point, pointIdx) =>
            `${pointIdx === 0 ? "M" : "L"} ${point.x} ${point.y}`,
        )
        .join(" ");

      paths.push(
        <path
          key={`${segment.key}-${idx}`}
          d={d}
          fill="none"
          stroke={strokeProps.color}
          strokeWidth={strokeWidth}
          strokeOpacity={strokeOpacity}
          strokeDasharray={getStrokeDasharray(strokeProps.dash)}
        />,
      );
    });

    const arrowSize = 8;
    const layerGap = arrowSize * 0.9;
    const baseGap = arrowSize * 0.6;
    const arrowLayers = 5;

    pivots.forEach((pivot, idx) => {
      const strokeProps = pivot.key === "up" ? upStrokeProps : downStrokeProps;
      const baseOpacity = strokeProps.opacity ?? 1;
      const pivotPoint = pivot?.point || { x: pivot?.x, y: pivot?.priceY };
      const x = Number.isFinite(pivotPoint?.x)
        ? pivotPoint.x
        : Number.isFinite(pivot?.x)
          ? pivot.x
          : undefined;
      const priceY = Number.isFinite(pivotPoint?.y)
        ? pivotPoint.y
        : Number.isFinite(pivot?.priceY)
          ? pivot.priceY
          : undefined;

      if (!Number.isFinite(x) || !Number.isFinite(priceY)) return;

      for (let layer = 0; layer < arrowLayers; layer += 1) {
        const offset = baseGap + layer * layerGap;
        const apexY = pivot.key === "up" ? priceY + offset : priceY - offset;
        const baseY =
          pivot.key === "up" ? apexY + arrowSize : apexY - arrowSize;
        const leftX = x - arrowSize * 0.75;
        const rightX = x + arrowSize * 0.75;
        const d = `M ${x} ${apexY} L ${leftX} ${baseY} L ${rightX} ${baseY} Z`;

        paths.push(
          <path
            key={`arrow-${pivot.key}-${idx}-${layer}`}
            d={d}
            fill={strokeProps.color}
            fillOpacity={
              hovering ? Math.min(1, baseOpacity + 0.15) : baseOpacity
            }
            stroke="none"
          />,
        );
      }
    });

    return (
      <g
        className={className}
        onDoubleClick={onDoubleClick}
        onContextMenu={onContextMenu}
      >
        {paths}
      </g>
    );
  }

  drawOnCanvas(ctx, moreProps) {
    const { upStroke, downStroke } = this.props;
    const upStrokeProps = this.normalizeStroke(upStroke, {
      color: "#8cc176",
      width: 2,
      dash: "Solid",
      opacity: 1,
    });
    const downStrokeProps = this.normalizeStroke(downStroke, {
      color: "#b82c0c",
      width: 2,
      dash: "Solid",
      opacity: 1,
    });

    const { segments, pivots } = this.getDirectionalSegments(moreProps);
    const { hovering } = moreProps;

    segments.forEach((segment) => {
      if (segment.points.length < 2) return;
      const strokeProps =
        segment.key === "up" ? upStrokeProps : downStrokeProps;
      ctx.beginPath();
      segment.points.forEach((point, idx) => {
        if (idx === 0) ctx.moveTo(point.x, point.y);
        else ctx.lineTo(point.x, point.y);
      });
      ctx.lineWidth = hovering
        ? (strokeProps.width + 1) * 1.5
        : strokeProps.width;
      ctx.strokeStyle = hexToRGBA(strokeProps.color, strokeProps.opacity);
      ctx.setLineDash(getStrokeDasharray(strokeProps.dash).split(","));
      ctx.stroke();
      ctx.setLineDash([]);
    });

    const arrowSize = 8;
    const layerGap = arrowSize * 0.9;
    const baseGap = arrowSize * 0.6;
    const arrowLayers = 5;

    pivots.forEach((pivot) => {
      const strokeProps = pivot.key === "up" ? upStrokeProps : downStrokeProps;
      const pivotPoint = pivot?.point || { x: pivot?.x, y: pivot?.priceY };
      const x = Number.isFinite(pivotPoint?.x)
        ? pivotPoint.x
        : Number.isFinite(pivot?.x)
          ? pivot.x
          : undefined;
      const priceY = Number.isFinite(pivotPoint?.y)
        ? pivotPoint.y
        : Number.isFinite(pivot?.priceY)
          ? pivot.priceY
          : undefined;

      if (!Number.isFinite(x) || !Number.isFinite(priceY)) return;

      for (let layer = 0; layer < arrowLayers; layer += 1) {
        const offset = baseGap + layer * layerGap;
        const apexY = pivot.key === "up" ? priceY + offset : priceY - offset;
        const baseY =
          pivot.key === "up" ? apexY + arrowSize : apexY - arrowSize;
        const leftX = x - arrowSize * 0.75;
        const rightX = x + arrowSize * 0.75;

        ctx.beginPath();
        ctx.moveTo(x, apexY);
        ctx.lineTo(leftX, baseY);
        ctx.lineTo(rightX, baseY);
        ctx.closePath();
        const arrowOpacity = hovering
          ? Math.min(1, (strokeProps.opacity ?? 1) + 0.15)
          : strokeProps.opacity;
        ctx.fillStyle = hexToRGBA(strokeProps.color, arrowOpacity);
        ctx.fill();
      }
    });
  }

  isHover(moreProps) {
    const {
      highlightOnHover,
      hoverTolerance,
      onHover,
      onUnHover,
      onContextMenu,
      onDoubleClick,
      onClick,
    } = this.props;

    const needsHover =
      highlightOnHover ||
      onHover ||
      onUnHover ||
      onContextMenu ||
      onDoubleClick ||
      onClick;
    if (!needsHover) return false;

    const tolerance = Number.isFinite(hoverTolerance) ? hoverTolerance : 6;
    const {
      mouseXY,
      chartConfig: { origin },
    } = moreProps;
    if (!mouseXY) return false;

    const { segments, pivots } = this.getDirectionalSegments(moreProps);
    if (!segments.length && !pivots.length) return false;

    const [mouseXRaw, mouseYRaw] = mouseXY;
    const pointer = {
      x: mouseXRaw + origin[0],
      y: mouseYRaw + origin[1],
    };

    const distanceToSegment = (p, v, w) => {
      const start = { x: v.x + origin[0], y: v.y + origin[1] };
      const end = { x: w.x + origin[0], y: w.y + origin[1] };
      const l2 = (end.x - start.x) ** 2 + (end.y - start.y) ** 2;
      if (l2 === 0) return Math.hypot(p.x - start.x, p.y - start.y);
      let t =
        ((p.x - start.x) * (end.x - start.x) +
          (p.y - start.y) * (end.y - start.y)) /
        l2;
      t = Math.max(0, Math.min(1, t));
      const projection = {
        x: start.x + t * (end.x - start.x),
        y: start.y + t * (end.y - start.y),
      };
      return Math.hypot(p.x - projection.x, p.y - projection.y);
    };

    for (let i = 0; i < segments.length; i += 1) {
      const segment = segments[i];
      if (!segment.points || segment.points.length < 2) continue;

      for (let j = 0; j < segment.points.length - 1; j += 1) {
        const start = segment.points[j];
        const end = segment.points[j + 1];
        const distance = distanceToSegment(pointer, start, end);
        if (distance <= tolerance) {
          return true;
        }
      }
    }

    if (pivots.length) {
      const arrowSize = 8;
      const layerGap = arrowSize * 0.9;
      const baseGap = arrowSize * 0.6;

      const pointInTriangle = (pt, a, b, c) => {
        const v0 = { x: c.x - a.x, y: c.y - a.y };
        const v1 = { x: b.x - a.x, y: b.y - a.y };
        const v2 = { x: pt.x - a.x, y: pt.y - a.y };

        const dot00 = v0.x * v0.x + v0.y * v0.y;
        const dot01 = v0.x * v1.x + v0.y * v1.y;
        const dot02 = v0.x * v2.x + v0.y * v2.y;
        const dot11 = v1.x * v1.x + v1.y * v1.y;
        const dot12 = v1.x * v2.x + v1.y * v2.y;

        const denom = dot00 * dot11 - dot01 * dot01;
        if (denom === 0) return false;
        const invDenom = 1 / denom;
        const u = (dot11 * dot02 - dot01 * dot12) * invDenom;
        const v = (dot00 * dot12 - dot01 * dot02) * invDenom;
        return u >= 0 && v >= 0 && u + v <= 1;
      };

      for (let i = 0; i < pivots.length; i += 1) {
        const pivot = pivots[i];
        const pivotPoint = pivot?.point || { x: pivot?.x, y: pivot?.priceY };
        if (!Number.isFinite(pivotPoint?.x) || !Number.isFinite(pivotPoint?.y))
          continue;

        const baseX = pivotPoint.x + origin[0];
        const baseY = pivotPoint.y + origin[1];

        for (let layer = 0; layer < 1; layer += 1) {
          const offset = baseGap + layer * layerGap;
          const apexY = pivot.key === "up" ? baseY + offset : baseY - offset;
          const bottomY =
            pivot.key === "up" ? apexY + arrowSize : apexY - arrowSize;
          const left = { x: baseX - arrowSize * 0.75, y: bottomY };
          const right = { x: baseX + arrowSize * 0.75, y: bottomY };
          const apex = { x: baseX, y: apexY };

          if (pointInTriangle(pointer, apex, left, right)) {
            return true;
          }
        }
      }
    }

    return false;
  }

  render() {
    const {
      clip,
      highlightOnHover,
      onHover,
      onUnHover,
      onContextMenu,
      onDoubleClick,
      onClick,
    } = this.props;
    const needsHover =
      highlightOnHover ||
      onHover ||
      onUnHover ||
      onContextMenu ||
      onDoubleClick ||
      onClick;
    const hoverProps = needsHover
      ? {
          isHover: this.isHover,
          drawOn: ["mousemove", "pan"],
          canvasToDraw: getMouseCanvas,
        }
      : {
          drawOn: ["pan"],
          canvasToDraw: getAxisCanvas,
        };
    return (
      <GenericChartComponent
        clip={clip}
        svgDraw={this.renderSVG}
        canvasDraw={this.drawOnCanvas}
        onHover={onHover}
        onUnHover={onUnHover}
        onClickWhenHover={onClick}
        onDoubleClickWhenHover={onDoubleClick}
        onContextMenuWhenHover={onContextMenu}
        {...hoverProps}
      />
    );
  }
}

SupertrendSeries.propTypes = {
  className: PropTypes.string,
  clip: PropTypes.bool,
  yAccessor: PropTypes.func.isRequired,
  upStroke: PropTypes.oneOfType([
    PropTypes.shape({
      color: PropTypes.string,
      width: PropTypes.number,
      dash: PropTypes.string,
      opacity: PropTypes.number,
    }),
    PropTypes.string,
  ]),
  downStroke: PropTypes.oneOfType([
    PropTypes.shape({
      color: PropTypes.string,
      width: PropTypes.number,
      dash: PropTypes.string,
      opacity: PropTypes.number,
    }),
    PropTypes.string,
  ]),
  onContextMenu: PropTypes.func,
  onDoubleClick: PropTypes.func,
  onHover: PropTypes.func,
  onUnHover: PropTypes.func,
  onClick: PropTypes.func,
  highlightOnHover: PropTypes.bool,
  hoverTolerance: PropTypes.number,
};

SupertrendSeries.defaultProps = {
  className: "react-stockcharts-supertrend-series",
  clip: true,
  upStroke: { color: "#8cc176", width: 2, dash: "Solid", opacity: 1 },
  downStroke: { color: "#b82c0c", width: 2, dash: "Solid", opacity: 1 },
  highlightOnHover: true,
  hoverTolerance: 6,
};

export default SupertrendSeries;
